import React, { FC, useMemo } from 'react';
import { PartOfCrpModalProps } from './PartOfCrpModalProps';
import Modal, { Prompt } from '@ingka/modal';
import Text from '@ingka/text';
import { IconButton, Stack } from '@mui/material';
import SSRIcon from '@ingka/ssr-icon';
import cross from '@ingka/ssr-icon/paths/cross';
import useTranslation from '../../hooks/useTranslation';
import PartOfCrpComboBox from '../PartOfCrpComboBox';
import { Box } from '@mui/system';
import Button from '@ingka/button';
import { getFyNumber } from '../../utils/calculation';

const PartOfCrpModal: FC<PartOfCrpModalProps> = ({
  visible,
  approveHandler,
  closeHandler,
  projects,
  selectedProjects,
}) => {
  const t = useTranslation();
  const [partOfCrp, setPartOfCrp] = React.useState<any>([]);

  const commonCrpForSelectedProjects = useMemo(() => {
    if (visible === false) {
      return [];
    }

    const selectedProjectIdSet = new Set(selectedProjects);
    const selectedProjectsArr =
      projects.filter((e) => selectedProjectIdSet.has(e.id)) || [];

    if (!selectedProjectsArr) {
      return [];
    }

    const commonFyArr = selectedProjectsArr.reduce(
      (acc, project) => {
        const projectFYs = project?.partOfCrp?.map((c) => c.fiscalYear) || [];
        return acc.filter((fy) => projectFYs.includes(fy));
      },
      selectedProjectsArr[0]?.partOfCrp?.map((c) => c.fiscalYear) || [],
    );

    commonFyArr.sort((a, b) => getFyNumber(a) - getFyNumber(b));

    return commonFyArr.map((fy) => ({ fiscalYear: fy }));
  }, [visible]);

  const commonAndSelectedCrpArr = useMemo(() => {
    const combinedArr = [...commonCrpForSelectedProjects, ...partOfCrp].filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.fiscalYear === item.fiscalYear),
    );
    combinedArr.sort(
      (a, b) => getFyNumber(a.fiscalYear) - getFyNumber(b.fiscalYear),
    );
    return combinedArr;
  }, [partOfCrp, commonCrpForSelectedProjects]);

  const close = () => {
    setPartOfCrp([]);
    closeHandler();
  };

  const confirm = () => {
    const fyArr = partOfCrp.map((e) => e.fiscalYear);
    setPartOfCrp([]);
    approveHandler(fyArr);
  };

  return (
    <Box
      sx={{
        '& .prompt__content-wrapper > div': {
          overflow: 'visible',
        },
        '& div[aria-labelledby="part-of-crp-modal-title"]': {
          overflow: 'visible',
        },
        '& .prompt__content-wrapper': {
          overflow: 'visible',
        },
      }}
    >
      <Modal
        visible={visible}
        handleCloseBtn={close}
        data-testid={'part-of-crp-modal'}
      >
        <Prompt
          header={<></>}
          titleId={'part-of-crp-modal-title'}
          title={
            <Stack mt={2}>
              <Stack
                alignSelf={'end'}
                position={'relative'}
                sx={{ right: '-15px' }}
              >
                <IconButton onClick={close}>
                  <SSRIcon paths={cross} />
                </IconButton>
              </Stack>
              <Stack mb={2}>{t('common.partOfCRPPlan')}</Stack>
              <Text style={{ fontSize: 16 }}>
                {t('common.partOfCRPPlanDescription1')}
              </Text>
              <Text style={{ fontSize: 16 }}>
                {t('common.partOfCRPPlanDescription2')}
              </Text>
            </Stack>
          }
          footer={<></>}
        >
          <Stack spacing={2} mb={2}>
            <PartOfCrpComboBox
              partOfCrp={commonAndSelectedCrpArr}
              handleUpdate={setPartOfCrp}
              label={t('labels.chooseCrpFy')}
            />
          </Stack>
          <Stack flexDirection={'row'} gap={2} mb={2}>
            <Button
              text={t('common.cancel')}
              data-testid={'crp-cancel'}
              type="secondary"
              onClick={close}
              fluid={true}
            />
            <Button
              text={t('common.confirm')}
              data-testid={'crp-confirm'}
              type="primary"
              onClick={confirm}
              fluid={true}
            />
          </Stack>
        </Prompt>
      </Modal>
    </Box>
  );
};

export default PartOfCrpModal;
